/**
 * ENG-5441 — circuit breaker for manager-initiated session restarts.
 *
 * Today's Scout incident on prod-integritylabs-agt-aws-1 respawned an
 * agent every ~55s for hours (~100 spawns/hr) because day-rollover
 * ([ENG-5431]) repeated indefinitely. Each respawn burned subscription
 * budget, flooded pane.log, drowned manager.log signal, and re-floods
 * `/host/direct-chat/poll`. The per-MCP reaper has a give-up cap
 * ([ENG-5279]) and the [ENG-5286] alarm fires when the cap-attempts
 * cross a threshold — but neither *stopped* the whole-agent loop.
 *
 * This breaker sits at the manager's spawn gate. Every manager-initiated
 * stop (day-rollover, auth-tuple change, reaper trigger, hot-reload
 * .mcp.json restart, channel-set restart) records a restart event. When
 * `> AGT_RESTART_BREAKER_MAX` events land inside the sliding
 * `AGT_RESTART_BREAKER_WINDOW_MS` window, the agent's breaker trips:
 * the manager stops respawning until an operator clears the trip (by
 * resuming the agent in the webapp / via the CLI).
 *
 * Pure state, no I/O — the caller (manager-worker.ts) owns serialisation
 * to `manager-state.json` so a manager restart preserves trip state and
 * doesn't accidentally clear a tripped breaker. Tests drive `now()` via
 * the constructor to avoid sleeping in CI.
 */

export interface RestartBreakerOptions {
  /** Max restart events allowed inside the window; the (max+1)th trips. Default 2 (env: AGT_RESTART_BREAKER_MAX). */
  max?: number;
  /** Sliding window in ms. Default 600_000 = 10 min (env: AGT_RESTART_BREAKER_WINDOW_MS). */
  windowMs?: number;
  /** Injectable clock for tests. */
  now?: () => number;
}

/** All known restart trigger sources. Adding a new source? Add it here so the trip detail is self-describing. */
export type RestartReason =
  | 'day-rollover'
  | 'auth-tuple-change'
  | 'model-change'
  | 'mcp-presence-reaper'
  | 'hot-reload-mcp'
  // ENG-7239: a .mcp.json drift restart whose changed server keys are ALL
  // managed-MCP/brokered toolkits (Composio and friends). Split from the
  // generic 'hot-reload-mcp' so the breaker carve-out for a flapping managed-MCP
  // set applies ONLY to managed-MCP churn, never to a co-occurring channel or
  // credential restart that happens to share the cool-off (CodeRabbit PR #2870).
  // Gated and respawn-verified identically to 'hot-reload-mcp'.
  | 'managed-mcp-churn'
  | 'channel-set-change'
  // ENG-5858: effective sender_policy flipped on either the org or
  // per-agent column. The slack-channel / teams-channel MCP children read
  // SLACK_SENDER_POLICY (and friends) from process.env once at boot, so
  // the rewrite of .mcp.json on its own doesn't propagate — the running
  // child keeps its stale env and stale gate. This restart class fires
  // independently of the .mcp.json value-only drift suppression
  // (ENG-5537) so composio URL rotation can't accidentally trigger it.
  | 'sender-policy-change'
  // ENG-6024: a Teams behaviour field (thread_auto_follow,
  // channel_response_mode, adaptive_cards_*) changed via the webapp's
  // Advanced panel. Same last-hop gap as sender-policy-change: the
  // teams-channel MCP child reads MSTEAMS_THREAD_AUTO_FOLLOW (and friends)
  // from process.env once at boot, so the .mcp.json rewrite alone doesn't
  // propagate. Keyed on a behaviour-subset hash, so token/credential churn
  // in the wider channel config can't fire it.
  | 'channel-behaviour-change'
  | 'manual'
  // ENG-5963: a channel MCP wrote a restart flag after an unrecoverable
  // runtime error. This is RECOVERY (a broken channel needs a restart to heal),
  // not non-urgent maintenance — so it is deliberately NOT in the
  // maintenance-window gate's allowlist and restarts immediately, while still
  // counting toward the restart breaker.
  | 'channel-restart-flag'
  | 'stale-mcp'
  // ENG-6203: a SESSION-level self-heal — the manager's restart-verifier reached
  // `unverified final` (a hot-reload restart that never respawned a healthy
  // session and never re-bound its MCP tools) and is forcing one more gated
  // re-respawn. Counts toward the breaker so a session that repeatedly fails to
  // respawn healthy is bounded (original restart + one remediation, then trip →
  // visible/circuit-tripped) rather than looping forever. Session-scoped, unlike
  // the per-server `mcp-presence-reaper` reason (which ENG-5547 keeps off the
  // breaker so one bad tool can't pause the whole agent).
  | 'bind-remediation'
  // ENG-6229: the in-session agent called `request_restart` after confirming
  // with the human (its existing ask_user HITL) that it needs a reload to pick
  // up a change it can SEE is missing but the manager's desired-state drift
  // detection structurally cannot (e.g. a realized-state MCP-binding fault, or
  // a hot-reload gap). RECOVERY class like `channel-restart-flag` — restarts
  // immediately, NOT held behind the maintenance window — because the agent
  // only asks when it's actively stuck serving someone. Counts toward the
  // breaker so a structurally-confused agent that keeps asking (a restart it
  // perceives as needed but that a restart cannot fix) is bounded and trips
  // into operator-cleared pause rather than looping.
  | 'agent-requested'
  | 'unknown';

/**
 * ENG-5547: decide whether an mcp-presence-reaper restart should count
 * toward the agent-wide circuit breaker.
 *
 * A restart attributable to a SINGLE persistently-failing MCP server is
 * already governed by that server's own ENG-5279 give-up cap (3 cycles →
 * the integration is marked unhealthy via /host/integration-health and
 * dropped from the next .mcp.json), which isolates the one broken tool and
 * lets the agent keep running degraded. Counting it would let the breaker
 * (default max 2) trip on the 3rd restart and pause the ENTIRE agent for one
 * bad integration — racing, and usually beating, the give-up that would have
 * isolated it. That sledgehammer is exactly what auto-paused phil.
 *
 * A restart where MULTIPLE distinct servers are simultaneously missing is a
 * genuine multi-cause storm the per-server cap can't see (the rotating-MCP
 * case ENG-5441 exists for), so it still counts.
 *
 * Returns the {@link RestartReason} to record (the restart counts toward the
 * breaker), or `undefined` to restart WITHOUT counting.
 */
export function reaperRestartBreakerReason(
  activeKeys: readonly string[],
): RestartReason | undefined {
  return activeKeys.length >= 2 ? 'mcp-presence-reaper' : undefined;
}

export interface RestartEvent {
  reason: RestartReason;
  at: number;
}

export interface TripState {
  trippedAt: number;
  eventsAtTrip: RestartEvent[];
  /** Pre-formatted `status_message` for the agents row + decision-log. */
  statusMessage: string;
}

export interface RecordResult {
  /** True on the call that flips the breaker from closed → tripped. */
  tripped: boolean;
  /** Always populated when `tripped` is true. */
  trip?: TripState;
  /** Current count inside the sliding window (post-record). */
  windowCount: number;
}

const DEFAULT_MAX = 2;
const DEFAULT_WINDOW_MS = 600_000;

export function readEnvNumber(name: string, fallback: number): number {
  const raw = process.env[name];
  if (!raw) return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Per-agent breaker. One instance per manager, keyed internally by code_name.
 * Held in module-level state in manager-worker.ts so it survives across
 * polls within a worker generation; trip state is mirrored to
 * `manager-state.json` so it survives across worker generations too.
 */
export class RestartBreaker {
  private readonly max: number;
  private readonly windowMs: number;
  private readonly now: () => number;
  private readonly events = new Map<string, RestartEvent[]>();
  private readonly trips = new Map<string, TripState>();

  constructor(opts: RestartBreakerOptions = {}) {
    this.max = opts.max ?? readEnvNumber('AGT_RESTART_BREAKER_MAX', DEFAULT_MAX);
    this.windowMs = opts.windowMs ?? readEnvNumber('AGT_RESTART_BREAKER_WINDOW_MS', DEFAULT_WINDOW_MS);
    this.now = opts.now ?? Date.now;
    // Reject NaN / Infinity explicitly — `??` treats them as present
    // values, so without these guards an operator who sets
    // `AGT_RESTART_BREAKER_MAX=NaN` (or a programmatic caller passing
    // `Number.NaN`) would silently disable the breaker rather than
    // fall through to the default. CodeRabbit on PR #1302.
    if (!Number.isFinite(this.max) || this.max < 1) {
      throw new Error('restart-breaker max must be a finite number >= 1');
    }
    if (!Number.isFinite(this.windowMs) || this.windowMs < 1000) {
      throw new Error('restart-breaker windowMs must be a finite number >= 1000');
    }
  }

  /** True if this agent's breaker is currently tripped (manager must skip spawn). */
  isTripped(codeName: string): boolean {
    return this.trips.has(codeName);
  }

  getTrip(codeName: string): TripState | undefined {
    return this.trips.get(codeName);
  }

  /**
   * Record a restart event. If recording this event puts the count
   * inside the window above `max`, the breaker trips and the call site
   * should NOT respawn.
   *
   * Idempotent on already-tripped breakers: returns the existing trip
   * without double-counting events. Callers may still record the reason
   * via the decision-log for forensics.
   */
  record(codeName: string, reason: RestartReason): RecordResult {
    const existing = this.trips.get(codeName);
    if (existing) {
      return { tripped: false, trip: existing, windowCount: existing.eventsAtTrip.length };
    }

    const at = this.now();
    const cutoff = at - this.windowMs;
    const prior = (this.events.get(codeName) ?? []).filter((e) => e.at >= cutoff);
    prior.push({ reason, at });
    this.events.set(codeName, prior);

    if (prior.length > this.max) {
      const trip: TripState = {
        trippedAt: at,
        eventsAtTrip: [...prior],
        statusMessage: formatStatusMessage(prior, this.windowMs),
      };
      this.trips.set(codeName, trip);
      // Free the events log — the trip carries the snapshot from now on.
      this.events.delete(codeName);
      return { tripped: true, trip, windowCount: prior.length };
    }

    return { tripped: false, windowCount: prior.length };
  }

  /** Operator-initiated reset: drops the trip + the events log for this agent. */
  clear(codeName: string): void {
    this.trips.delete(codeName);
    this.events.delete(codeName);
  }

  /** Snapshot tripped agents for `manager-state.json`. */
  serialize(): Record<string, TripState> {
    return Object.fromEntries(this.trips.entries());
  }

  /**
   * Rehydrate trip state from `manager-state.json`. Called once at
   * worker startup. Window history is intentionally NOT persisted — only
   * tripped state. A tripped breaker survives manager restart; an
   * un-tripped one starts a fresh window in the new worker.
   */
  hydrate(saved: Record<string, TripState> | null | undefined): void {
    if (!saved) return;
    for (const [codeName, trip] of Object.entries(saved)) {
      if (trip && typeof trip.trippedAt === 'number' && Array.isArray(trip.eventsAtTrip)) {
        this.trips.set(codeName, trip);
      }
    }
  }

  /** Test helper — current in-window event count for `codeName`. */
  windowCount(codeName: string): number {
    const cutoff = this.now() - this.windowMs;
    return (this.events.get(codeName) ?? []).filter((e) => e.at >= cutoff).length;
  }
}

function formatStatusMessage(events: RestartEvent[], windowMs: number): string {
  const last = events[events.length - 1]!;
  // Render the window in seconds for sub-minute thresholds (operator-
  // tuned via AGT_RESTART_BREAKER_WINDOW_MS) and in decimal minutes
  // otherwise. Math.round(/60_000) was returning "0min" for a 30s
  // tuning, which is unhelpful in the dashboard. CodeRabbit on PR #1302.
  const windowLabel = windowMs < 60_000
    ? `${Math.round(windowMs / 1000)}s`
    : `${(windowMs / 60_000).toFixed(1).replace(/\.0$/, '')}min`;
  const reasonCounts = new Map<RestartReason, number>();
  for (const e of events) reasonCounts.set(e.reason, (reasonCounts.get(e.reason) ?? 0) + 1);
  const breakdown = Array.from(reasonCounts.entries())
    .map(([r, n]) => `${r}=${n}`)
    .join(', ');
  return (
    `Circuit breaker tripped: ${events.length} restarts in ${windowLabel} ` +
    `(${breakdown}); most recent=${last.reason} at ${new Date(last.at).toISOString()}`
  );
}
